import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Buildings,
  Bell,
  Lightning,
  ArrowRight,
  CheckCircle,
  CreditCard,
  Calendar,
  Envelope,
  ChartLine,
  Image,
  Trash,
  UploadSimple,
  SpinnerGap,
  PencilSimple,
  Eye,
  Wrench,
  Package,
} from "phosphor-react";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { useEntitlementsWithFetch } from "../contexts/EntitlementContext";
import AdBanner from "../components/AdBanner";
import { CustomAdSlot } from "../components/CustomAdSlot";
import { authFetch, authUploadFile, authFetchJson } from "../auth/authApi";
import CatalogManagement from "./CatalogManagement";
import NotificationPreferences from "../components/NotificationPreferences";
import Technicians from "./Technicians";
import SmartToolsUsageMeter from "../components/SmartToolsUsageMeter";
import PaymentsActivationCard from "../components/PaymentsActivationCard";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Settings() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { isFeatureActive, isEntitled, shouldShowUpgradeCTA, fetched, entitled_slugs } = useEntitlementsWithFetch();
  
  // Get the active tab from URL params, default to "company"
  const activeTab = searchParams.get("tab") || "company";
  
  // Handle tab changes - update URL params
  const handleTabChange = (value) => {
    if (value === "company") {
      // Remove tab param for default
      setSearchParams({});
    } else {
      setSearchParams({ tab: value });
    }
  };

  // Form states - using structured address fields as source of truth
  const [companyForm, setCompanyForm] = useState({
    company_name: "",
    industry: "general",
    phone: "",
    address_street: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    tax_rate_percent: "",
    calendar_business_hours_start: "07:00",
    calendar_business_hours_end: "19:00",
  });

  // Logo states
  const [logoInfo, setLogoInfo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const fileInputRef = useRef(null);

  // Email templates states
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    subject: "",
    greeting: "",
    body: "",
    cta_text: "",
    footer: "",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [sendingTestEmail, setSendingTestEmail] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchLogoInfo();
    fetchEmailTemplates();
  }, []);

  const fetchLogoInfo = async () => {
    try {
      // Use authFetchJson for Bearer token authentication
      const data = await authFetchJson('/tenants/logo');
      setLogoInfo(data);
    } catch (error) {
      console.error("Error fetching logo:", error);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      toast.error("Please upload a PNG or JPG image");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setUploadingLogo(true);

    try {
      // Use authUploadFile for Bearer token authenticated upload
      const data = await authUploadFile('/tenants/logo/upload', file);
      
      if (data.success) {
        toast.success("Logo uploaded successfully!");
        setLogoInfo({
          has_logo: true,
          logo_url: data.logo_url,
          thumbnail_url: data.thumbnail_url,
        });
      } else {
        toast.error(data.detail || "Failed to upload logo");
      }
    } catch (error) {
      toast.error(error.message || "Failed to upload logo");
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!confirm("Are you sure you want to delete your company logo?")) return;

    setDeletingLogo(true);
    try {
      // Use authFetch for Bearer token authenticated DELETE request
      const response = await authFetch('/tenants/logo', {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success("Logo deleted");
        setLogoInfo({ has_logo: false });
      } else {
        toast.error("Failed to delete logo");
      }
    } catch (error) {
      toast.error("Failed to delete logo");
    } finally {
      setDeletingLogo(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const tenantRes = await fetch(`${API}/tenants/current`, { credentials: "include" });

      if (tenantRes.ok) {
        const tenantData = await tenantRes.json();
        setTenant(tenantData);
        
        // Use structured address fields if available, otherwise fall back to legacy
        const hasStructuredAddress = tenantData.address_street || tenantData.address_city || 
                                     tenantData.address_state || tenantData.address_zip;
        
        setCompanyForm({
          company_name: tenantData.company_name || "",
          industry: tenantData.industry || "general",
          phone: tenantData.phone || "",
          // Structured address fields (canonical source of truth)
          address_street: tenantData.address_street || "",
          address_city: tenantData.address_city || "",
          address_state: tenantData.address_state || "",
          address_zip: tenantData.address_zip || "",
          tax_rate_percent: tenantData.tax_rate_percent ?? "",
          calendar_business_hours_start: tenantData.calendar_business_hours_start || "07:00",
          calendar_business_hours_end: tenantData.calendar_business_hours_end || "19:00",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveCompanySettings = async () => {
    setSaving(true);
    try {
      // Validate business hours
      if (companyForm.calendar_business_hours_start && companyForm.calendar_business_hours_end) {
        const startParts = companyForm.calendar_business_hours_start.split(":").map(Number);
        const endParts = companyForm.calendar_business_hours_end.split(":").map(Number);
        const startMinutes = startParts[0] * 60 + startParts[1];
        const endMinutes = endParts[0] * 60 + endParts[1];
        
        if (endMinutes <= startMinutes) {
          toast.error("Business hours end time must be after start time");
          setSaving(false);
          return;
        }
      }

      // Build payload with structured address fields
      const payload = {
        company_name: companyForm.company_name,
        industry: companyForm.industry,
        phone: companyForm.phone,
        // Structured address fields (canonical)
        address_street: companyForm.address_street,
        address_city: companyForm.address_city,
        address_state: companyForm.address_state?.toUpperCase(),
        address_zip: companyForm.address_zip,
        tax_rate_percent: companyForm.tax_rate_percent === "" ? null : parseFloat(companyForm.tax_rate_percent) || 0,
        calendar_business_hours_start: companyForm.calendar_business_hours_start,
        calendar_business_hours_end: companyForm.calendar_business_hours_end,
      };
      
      const response = await fetch(`${API}/tenants/current`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success("Company settings saved");
        fetchSettings();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save settings");
      }
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Email template functions
  const fetchEmailTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError(null);
    try {
      const response = await fetch(`${API}/settings/comm-templates`, { credentials: "include" });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      
      // Support both response shapes: raw array OR { templates: [...] }
      const rawTemplates = Array.isArray(data) ? data : 
                           Array.isArray(data?.templates) ? data.templates : [];
      
      // Map API format to expected format and separate by cost_class
      const templates = rawTemplates.map(t => ({
        template_type: t.template_key,
        name: t.display_name,
        description: t.description || "",
        subject: t.effective?.subject || "",
        greeting: t.effective?.greeting || "",
        body: t.effective?.body || "",
        cta_text: t.effective?.button_text || "",
        footer: t.effective?.footer || "",
        is_customized: t.has_override,
        requires_entitlement: t.requires_entitlement,
        cost_class: t.requires_entitlement ? "marketing" : "transactional",
        channel: t.channel,
        enabled: t.enabled,
        variables: t.allowed_variables || [],
      }));
      setEmailTemplates(templates);
    } catch (error) {
      console.error("Failed to load comm templates:", error);
      setEmailTemplates([]);
      setTemplatesError(error?.message || "Failed to load templates");
    } finally {
      setTemplatesLoading(false);
    }
  };

  const openTemplateEditor = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      subject: template.subject || "",
      greeting: template.greeting || "Hi {{customer_first_name}},",
      body: template.body || "",
      cta_text: template.cta_text || "Learn More",
      footer: template.footer || "Thank you for your business!",
    });
    setPreviewHtml("");
  };

  const closeTemplateEditor = () => {
    setEditingTemplate(null);
    setTemplateForm({ subject: "", greeting: "", body: "", cta_text: "", footer: "" });
    setPreviewHtml("");
  };

  const previewTemplate = async () => {
    if (!editingTemplate) return;
    
    try {
      const response = await fetch(`${API}/settings/comm-templates/${editingTemplate.template_type}/preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: templateForm.subject,
          greeting: templateForm.greeting,
          body: templateForm.body,
          button_text: templateForm.cta_text,
          footer: templateForm.footer,
          context: {
            customer_first_name: "John",
            customer_name: "John Smith",
            company_name: tenant?.company_name || "Your Company",
          }
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPreviewHtml(data.html_preview);
      } else {
        toast.error("Failed to generate preview");
      }
    } catch (error) {
      toast.error("Failed to generate preview");
    }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    
    // Validation
    if (!templateForm.subject.trim()) {
      toast.error("Please enter a subject line");
      return;
    }
    if (!templateForm.body.trim()) {
      toast.error("Please enter the email body");
      return;
    }
    
    setSavingTemplate(true);
    try {
      const response = await fetch(`${API}/settings/comm-templates/${editingTemplate.template_type}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subject: templateForm.subject,
          greeting: templateForm.greeting,
          body: templateForm.body,
          button_text: templateForm.cta_text,
          footer: templateForm.footer,
        }),
      });

      if (response.ok) {
        toast.success("Template saved");
        fetchEmailTemplates();
        closeTemplateEditor();
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to save template");
      }
    } catch (error) {
      toast.error("Failed to save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  const resetTemplate = async () => {
    if (!editingTemplate) return;
    try {
      const response = await fetch(`${API}/tenants/email-templates/${editingTemplate.template_type}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Template reset to default");
        fetchEmailTemplates();
        closeTemplateEditor();
      } else {
        toast.error("Failed to reset template");
      }
    } catch (error) {
      toast.error("Failed to reset template");
    }
  };

  const sendTestEmail = async () => {
    if (!editingTemplate) return;
    
    setSendingTestEmail(true);
    try {
      const response = await fetch(`${API}/tenants/email-templates/${editingTemplate.template_type}/send-test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(templateForm),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message || "Test email sent!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to send test email");
      }
    } catch (error) {
      toast.error("Failed to send test email");
    } finally {
      setSendingTestEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-48 bg-zinc-200 rounded-sm animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Feature Upgrades Card */}
      <Card className="border-2 border-[#FF6600]/30 bg-gradient-to-r from-[#FF6600]/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-[#FF6600]/20 flex items-center justify-center">
                <Lightning size={24} className="text-[#FF6600]" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold uppercase">
                  Feature Upgrades
                </h3>
                <p className="text-sm text-zinc-600">
                  Manage your subscription and add powerful features
                </p>
                {entitled_slugs?.length > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <CheckCircle size={14} className="text-emerald-500" />
                    <span className="text-xs text-zinc-500">
                      {entitled_slugs.length} active feature(s)
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                onClick={() => navigate("/settings/subscription")}
                variant="outline"
                className="border-2"
                data-testid="manage-subscriptions-btn"
              >
                <Lightning size={16} className="mr-2" />
                Manage Subscriptions
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate("/settings/billing")}
                className="border-2"
                data-testid="billing-btn"
              >
                <CreditCard size={16} className="mr-2" />
                Billing
              </Button>
              <Button
                onClick={() => navigate("/settings/upgrades")}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="add-features-btn"
              >
                <ArrowRight size={16} className="mr-2" />
                Add Features
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="border-2 border-zinc-200 p-1 bg-zinc-50 flex flex-wrap h-auto gap-1 overflow-visible justify-start">
          <TabsTrigger value="company" className="data-[state=active]:bg-white flex-shrink">
            <Buildings size={16} className="mr-2" />
            Company
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-white flex-shrink">
            <Image size={16} className="mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="catalog" className="data-[state=active]:bg-white flex-shrink">
            <Package size={16} className="mr-2" />
            Catalog
          </TabsTrigger>
          <TabsTrigger value="technicians" className="data-[state=active]:bg-white flex-shrink">
            <Wrench size={16} className="mr-2" />
            Technicians
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white flex-shrink">
            <Bell size={16} className="mr-2" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="email-templates" className="data-[state=active]:bg-white flex-shrink">
            <Envelope size={16} className="mr-2" />
            Customer Messages
          </TabsTrigger>
          <TabsTrigger value="smart-tools" className="data-[state=active]:bg-white flex-shrink">
            <Lightning size={16} className="mr-2" />
            Smart Tools
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-white flex-shrink">
            <CreditCard size={16} className="mr-2" />
            Payments
          </TabsTrigger>
        </TabsList>

        {/* Company Settings */}
        <TabsContent value="company">
          <Card className="border-2 border-zinc-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase">
                Company Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Company Name</Label>
                <Input
                  value={companyForm.company_name}
                  onChange={(e) =>
                    setCompanyForm({ ...companyForm, company_name: e.target.value })
                  }
                  className="mt-1 h-12 border-2"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={companyForm.phone}
                  onChange={(e) =>
                    setCompanyForm({ ...companyForm, phone: e.target.value })
                  }
                  placeholder="(555) 123-4567"
                  className="mt-1 h-12 border-2"
                  data-testid="company-phone-input"
                />
              </div>
              
              {/* Structured Address Fields */}
              <div className="space-y-3">
                <Label>Business Address</Label>
                <Input
                  value={companyForm.address_street}
                  onChange={(e) =>
                    setCompanyForm({ ...companyForm, address_street: e.target.value })
                  }
                  placeholder="Street address"
                  className="h-12 border-2"
                  data-testid="company-street-input"
                />
                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-3">
                    <Input
                      value={companyForm.address_city}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, address_city: e.target.value })
                      }
                      placeholder="City"
                      className="h-12 border-2"
                      data-testid="company-city-input"
                    />
                  </div>
                  <div className="col-span-1">
                    <Input
                      value={companyForm.address_state}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, address_state: e.target.value.toUpperCase().slice(0, 2) })
                      }
                      placeholder="ST"
                      maxLength={2}
                      className="h-12 border-2 text-center uppercase"
                      data-testid="company-state-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={companyForm.address_zip}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, address_zip: e.target.value })
                      }
                      placeholder="ZIP"
                      className="h-12 border-2"
                      data-testid="company-zip-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-zinc-500">
                  This address appears on your invoices and estimates.
                </p>
              </div>
              
              <div>
                <Label>Tax Rate (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={companyForm.tax_rate_percent}
                  onChange={(e) =>
                    setCompanyForm({ ...companyForm, tax_rate_percent: e.target.value })
                  }
                  placeholder="e.g., 8.25"
                  className="mt-1 h-12 border-2"
                  data-testid="company-tax-rate-input"
                />
                <p className="text-xs text-zinc-500 mt-1">
                  This rate will be applied to taxable items on estimates
                </p>
              </div>
              
              {/* Calendar Business Hours */}
              <div className="border-t pt-4 mt-4">
                <Label className="text-base font-semibold flex items-center gap-2 mb-3">
                  <Calendar size={18} />
                  Calendar Business Hours
                </Label>
                <p className="text-sm text-zinc-500 mb-4">
                  Set the visible time range for your Day and Week calendar views.
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={companyForm.calendar_business_hours_start}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, calendar_business_hours_start: e.target.value })
                      }
                      className="mt-1 h-12 border-2"
                      data-testid="business-hours-start-input"
                    />
                  </div>
                  <div>
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={companyForm.calendar_business_hours_end}
                      onChange={(e) =>
                        setCompanyForm({ ...companyForm, calendar_business_hours_end: e.target.value })
                      }
                      className="mt-1 h-12 border-2"
                      data-testid="business-hours-end-input"
                    />
                  </div>
                </div>
                <p className="text-xs text-zinc-500 mt-2">
                  Default: 07:00 - 19:00. This affects Day and Week calendar views only.
                </p>
              </div>

              <Button
                onClick={saveCompanySettings}
                disabled={saving}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white mt-4"
                data-testid="save-company-btn"
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Branding Settings */}
        <TabsContent value="branding">
          <Card className="border-2 border-zinc-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase">
                Company Logo
              </CardTitle>
              <CardDescription>
                Upload your company logo to display in email marketing campaigns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Logo Display */}
              {logoInfo?.has_logo ? (
                <div className="space-y-4">
                  <div className="flex items-start gap-6 p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                    <div className="flex-shrink-0">
                      <img
                        src={logoInfo.logo_url}
                        alt="Company logo"
                        className="max-w-[200px] max-h-[80px] object-contain rounded border border-zinc-200 bg-white p-2"
                        data-testid="current-logo"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-zinc-700 mb-1">Current Logo</p>
                      <p className="text-xs text-zinc-500 mb-3">
                        This logo will be displayed in your email marketing campaigns.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="border-2"
                        >
                          {uploadingLogo ? (
                            <SpinnerGap size={14} className="animate-spin mr-2" />
                          ) : (
                            <UploadSimple size={14} className="mr-2" />
                          )}
                          Replace
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDeleteLogo}
                          disabled={deletingLogo}
                          className="border-2 text-red-600 hover:bg-red-50 hover:text-red-700"
                        >
                          {deletingLogo ? (
                            <SpinnerGap size={14} className="animate-spin mr-2" />
                          ) : (
                            <Trash size={14} className="mr-2" />
                          )}
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-zinc-300 rounded-lg p-8 text-center hover:border-[#FF6600]/50 transition-colors">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-zinc-100 flex items-center justify-center">
                    <Image size={32} className="text-zinc-400" />
                  </div>
                  <h3 className="text-lg font-medium text-zinc-700 mb-2">
                    Upload Your Company Logo
                  </h3>
                  <p className="text-sm text-zinc-500 mb-4">
                    PNG or JPG, max 5MB. Will be resized for optimal email display.
                  </p>
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingLogo}
                    className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                    data-testid="upload-logo-btn"
                  >
                    {uploadingLogo ? (
                      <>
                        <SpinnerGap size={16} className="animate-spin mr-2" />
                        Uploading...
                      </>
                    ) : (
                      <>
                        <UploadSimple size={16} className="mr-2" />
                        Choose File
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg"
                onChange={handleLogoUpload}
                className="hidden"
                data-testid="logo-file-input"
              />

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-800 text-sm mb-2 flex items-center gap-2">
                  <CheckCircle size={16} />
                  Logo Best Practices
                </h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Use a horizontal logo for best email display</li>
                  <li>• Transparent PNG backgrounds work great</li>
                  <li>• Logo will be automatically resized to max 300×100px</li>
                  <li>• Keep file size under 5MB for fastest upload</li>
                </ul>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Catalog Management */}
        <TabsContent value="catalog">
          <CatalogManagement embedded={true} />
        </TabsContent>

        {/* Technicians - Embedded directly */}
        <TabsContent value="technicians">
          <Technicians />
        </TabsContent>

        {/* Notification Settings - Contractor Communication Preferences */}
        <TabsContent value="notifications">
          <NotificationPreferences />
        </TabsContent>


        {/* Customer Messages */}
        <TabsContent value="email-templates">
          <Card className="border-2 border-zinc-200">
            <CardHeader>
              <CardTitle className="font-heading text-lg uppercase">
                Customer Messages
              </CardTitle>
              <p className="text-sm text-zinc-500 mt-1">
                Customize the emails and SMS sent to your customers
              </p>
            </CardHeader>
            <CardContent className="space-y-8">
              {templatesLoading ? (
                <div className="text-center py-8 text-zinc-500">
                  <SpinnerGap size={48} className="mx-auto mb-4 opacity-50 animate-spin" />
                  <p>Loading templates...</p>
                </div>
              ) : templatesError ? (
                <div className="text-center py-8 text-red-500">
                  <Envelope size={48} className="mx-auto mb-4 opacity-50" />
                  <p className="font-medium">Failed to load templates</p>
                  <p className="text-sm mt-1">{templatesError}</p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={fetchEmailTemplates}
                  >
                    Retry
                  </Button>
                </div>
              ) : emailTemplates.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <Envelope size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No templates available</p>
                </div>
              ) : (
                <>
                  {/* Transactional Templates Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                      Transactional Messages
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">
                      Essential business communications sent automatically
                    </p>
                    <div className="space-y-3">
                      {/* Enforce order: estimate, invoice, payment, appointment.email, appointment.sms */}
                      {["estimate.sent.email", "invoice.sent.email", "payment.receipt.email", "appointment.reminder.email", "appointment.reminder.sms"]
                        .map(key => emailTemplates.find(t => t.template_type === key))
                        .filter(Boolean)
                        .map((template) => (
                          <div
                            key={template.template_type}
                            className="flex items-center justify-between p-4 border border-zinc-200 rounded-lg hover:border-[#FF6600]/50 transition-colors bg-white"
                            data-testid={`template-${template.template_type}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium">{template.name}</h4>
                                {template.channel === "sms" && (
                                  <Badge variant="outline" className="text-xs text-blue-600 border-blue-300 bg-blue-50">
                                    SMS
                                  </Badge>
                                )}
                                {template.is_customized && (
                                  <Badge variant="outline" className="text-xs text-[#FF6600] border-[#FF6600]/30 bg-[#FF6600]/5">
                                    Customized
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-zinc-500 mt-0.5">{template.description}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-2 ml-4"
                              onClick={() => openTemplateEditor(template)}
                              data-testid={`edit-template-${template.template_type}`}
                            >
                              <PencilSimple size={14} className="mr-1" />
                              Edit
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>

                  {/* Paid Templates Section */}
                  <div className="pt-4 border-t border-zinc-200">
                    <h3 className="text-sm font-semibold text-zinc-700 uppercase tracking-wide mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      Email Marketing
                    </h3>
                    <p className="text-xs text-zinc-500 mb-4">
                      Marketing and review collection communications
                    </p>
                    <div className="space-y-3">
                      {/* Enforce order: sentiment, public_link, private_feedback, marketing */}
                      {["review.sentiment.email", "review.public_link.email", "review.private_feedback.email", "marketing.broadcast.email"]
                        .map(key => emailTemplates.find(t => t.template_type === key))
                        .filter(Boolean)
                        .map((template) => (
                          <div
                            key={template.template_type}
                            className="flex items-center justify-between p-4 border border-zinc-200 rounded-lg hover:border-[#FF6600]/50 transition-colors bg-white"
                            data-testid={`template-${template.template_type}`}
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium">{template.name}</h4>
                                <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50">
                                  Requires Email Marketing
                                </Badge>
                                {template.is_customized && (
                                  <Badge variant="outline" className="text-xs text-[#FF6600] border-[#FF6600]/30 bg-[#FF6600]/5">
                                    Customized
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-zinc-500 mt-0.5">{template.description}</p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              className="border-2 ml-4"
                              onClick={() => openTemplateEditor(template)}
                              data-testid={`edit-template-${template.template_type}`}
                            >
                              <PencilSimple size={14} className="mr-1" />
                              Edit
                            </Button>
                          </div>
                        ))}
                    </div>
                  </div>
                </>
              )}

              {/* Template variables info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                <h4 className="font-medium text-blue-800 text-sm mb-2">Available Variables</h4>
                <p className="text-xs text-blue-700 mb-2">
                  Use these placeholders in your templates - they&apos;ll be replaced with actual values when emails are sent:
                </p>
                <div className="flex flex-wrap gap-2">
                  {["{{customer_first_name}}", "{{customer_name}}", "{{company_name}}"].map((v) => (
                    <code key={v} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      {v}
                    </code>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Smart Tools Usage */}
        <TabsContent value="smart-tools">
          <div className="max-w-md">
            <SmartToolsUsageMeter hasSmartTools={isEntitled("smart_tools")} />
          </div>
        </TabsContent>

        {/* Payments / Finix Setup - Now using state-driven activation card */}
        <TabsContent value="payments">
          <PaymentsActivationCard tenant={tenant} onRefresh={fetchSettings} />
        </TabsContent>
      </Tabs>

      {/* Email Template Editor Modal - Gold Standard Spacing */}
      <Dialog open={!!editingTemplate} onOpenChange={(open) => !open && closeTemplateEditor()}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <DialogHeader className="px-8 pt-8 pb-0">
            <DialogTitle className="font-heading text-xl uppercase tracking-wide">
              Edit {editingTemplate?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500 mt-1">
              Customize this message template to match your brand voice
            </DialogDescription>
          </DialogHeader>
          {editingTemplate && (
            <div className="px-8 py-6 space-y-6">
              {/* Editor Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Form Column */}
                <div className="space-y-5">
                  {/* Subject Line - hidden for SMS */}
                  {editingTemplate.channel !== "sms" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-zinc-700">Subject Line *</Label>
                      <Input
                        value={templateForm.subject}
                        onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
                        className="h-11 border-2 border-zinc-200 focus:border-[#FF6600] transition-colors"
                        placeholder="Email subject..."
                        data-testid="template-subject-input"
                      />
                      <p className="text-xs text-zinc-400">
                        Variables: {"{{company_name}}"}, {"{{customer_name}}"}
                      </p>
                    </div>
                  )}

                  {/* Greeting - hidden for SMS */}
                  {editingTemplate.channel !== "sms" && (
                    <div className="space-y-2">
                      <Label className="text-sm font-medium text-zinc-700">Greeting</Label>
                      <Input
                        value={templateForm.greeting}
                        onChange={(e) => setTemplateForm({ ...templateForm, greeting: e.target.value })}
                        className="h-11 border-2 border-zinc-200 focus:border-[#FF6600] transition-colors"
                        placeholder="Hi {{customer_first_name}},"
                        data-testid="template-greeting-input"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-zinc-700">
                      {editingTemplate.channel === "sms" ? "Message *" : "Body *"}
                    </Label>
                    <Textarea
                      value={templateForm.body}
                      onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                      className="border-2 border-zinc-200 focus:border-[#FF6600] transition-colors min-h-[120px]"
                      rows={5}
                      placeholder={editingTemplate.channel === "sms" ? "SMS message content..." : "Main message content..."}
                      data-testid="template-body-input"
                    />
                    {editingTemplate.channel === "sms" && (
                      <p className="text-xs text-zinc-400">
                        SMS messages are limited to 160 characters for best delivery
                      </p>
                    )}
                  </div>

                  {/* Button/Footer - hidden for SMS */}
                  {editingTemplate.channel !== "sms" && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-zinc-700">Button Text</Label>
                        <Input
                          value={templateForm.cta_text}
                          onChange={(e) => setTemplateForm({ ...templateForm, cta_text: e.target.value })}
                          className="h-11 border-2 border-zinc-200 focus:border-[#FF6600] transition-colors"
                          placeholder="View Details"
                          data-testid="template-cta-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-zinc-700">Footer</Label>
                        <Input
                          value={templateForm.footer}
                          onChange={(e) => setTemplateForm({ ...templateForm, footer: e.target.value })}
                          className="h-11 border-2 border-zinc-200 focus:border-[#FF6600] transition-colors"
                          placeholder="Thank you!"
                          data-testid="template-footer-input"
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3 pt-2">
                    <Button 
                      variant="outline" 
                      onClick={previewTemplate} 
                      className="flex-1 h-11 border-2 border-zinc-200 hover:border-[#FF6600] hover:bg-[#FF6600]/5"
                    >
                      <Eye size={16} className="mr-2" />
                      Preview
                    </Button>
                    {editingTemplate.channel !== "sms" && (
                      <Button 
                        variant="outline" 
                        onClick={sendTestEmail} 
                        disabled={sendingTestEmail}
                        className="flex-1 h-11 border-2 border-zinc-200 hover:border-[#FF6600] hover:bg-[#FF6600]/5"
                        data-testid="send-test-email-btn"
                      >
                        {sendingTestEmail ? (
                          <>
                            <SpinnerGap className="animate-spin mr-2" size={16} />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Envelope size={16} className="mr-2" />
                            Send Test
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Preview Column */}
                <div className="flex flex-col">
                  <Label className="text-sm font-medium text-zinc-700 mb-3">
                    {editingTemplate.channel === "sms" ? "SMS Preview" : "Email Preview"}
                  </Label>
                  <div className="flex-1 border-2 border-zinc-200 rounded-lg bg-zinc-50 p-4 min-h-[400px]">
                    {previewHtml ? (
                      <div
                        className={editingTemplate.channel === "sms" 
                          ? "bg-white rounded-lg border border-zinc-200 p-4 max-w-[300px] mx-auto shadow-sm"
                          : "bg-white rounded-lg border border-zinc-200 overflow-hidden shadow-sm"
                        }
                        dangerouslySetInnerHTML={{ __html: previewHtml }}
                      />
                    ) : (
                      <div className="h-full flex flex-col items-center justify-center text-zinc-400">
                        <Envelope size={40} className="mb-3 opacity-40" />
                        <p className="text-sm text-center">
                          Click &quot;Preview&quot; to see<br />how your template will look
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Variables reference */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-lg p-4">
                <Label className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-3 block">
                  Available Variables
                </Label>
                <div className="flex flex-wrap gap-2">
                  {(editingTemplate.variables || ["customer_first_name", "customer_name", "company_name"]).map((v) => (
                    <code key={v} className="text-xs bg-white text-zinc-600 px-3 py-1.5 rounded-md border border-zinc-200 font-mono">
                      {`{{${v}}}`}
                    </code>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between items-center pt-4 border-t border-zinc-200">
                <div>
                  {editingTemplate.is_customized && (
                    <Button 
                      variant="ghost" 
                      onClick={resetTemplate} 
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 h-11"
                    >
                      Reset to Default
                    </Button>
                  )}
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={closeTemplateEditor} 
                    className="h-11 px-6 border-2 border-zinc-200"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveTemplate}
                    disabled={savingTemplate}
                    className="h-11 px-6 bg-[#FF6600] hover:bg-[#CC5200] text-white font-medium"
                    data-testid="save-template-btn"
                  >
                    {savingTemplate ? (
                      <>
                        <SpinnerGap className="animate-spin mr-2" size={16} />
                        Saving...
                      </>
                    ) : (
                      "Save Template"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Entitlements Debug Panel (Dev/Admin Only) */}
      {(tenant?.debug_mode || window.location.search.includes('debug=1')) && (
        <Card className="border-2 border-dashed border-amber-400 bg-amber-50/50 mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-sm uppercase text-amber-700 flex items-center gap-2">
              <Eye size={16} />
              Entitlements Debug
            </CardTitle>
            <CardDescription className="text-xs text-amber-600">
              Shows real-time entitlement state for troubleshooting
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-amber-700 font-medium">Tenant ID:</span>
                <code className="ml-2 text-xs bg-white px-2 py-1 rounded">{tenant?.tenant_id || 'N/A'}</code>
              </div>
              <div>
                <span className="text-amber-700 font-medium">Fetched:</span>
                <code className="ml-2 text-xs bg-white px-2 py-1 rounded">{fetched ? 'Yes' : 'No'}</code>
              </div>
            </div>
            <div>
              <span className="text-amber-700 font-medium text-sm">email_marketing entitled:</span>
              <Badge 
                className={`ml-2 ${isEntitled('email_marketing') ? 'bg-green-500' : 'bg-red-500'} text-white`}
              >
                {isEntitled('email_marketing') ? 'TRUE' : 'FALSE'}
              </Badge>
            </div>
            <div>
              <span className="text-amber-700 font-medium text-sm">All Entitled Slugs:</span>
              <div className="mt-1 bg-white rounded p-2 text-xs font-mono overflow-x-auto">
                {entitled_slugs?.length > 0 
                  ? entitled_slugs.join(', ')
                  : '(none)'}
              </div>
            </div>
            <div className="flex gap-2 pt-2 border-t border-amber-300">
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={async () => {
                  try {
                    const res = await authFetchJson('/entitlements/debug');
                    console.log('[Entitlements Debug]', res);
                    toast.info('Debug data logged to console');
                  } catch (e) {
                    toast.error('Failed to fetch debug data');
                  }
                }}
              >
                Log Full Debug
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                onClick={() => {
                  window.location.reload();
                }}
              >
                Force Refresh
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ad Banner - Bottom of Settings page */}
      <AdBanner placement="settings_footer" />

      {/* Custom Ad Slot - Settings Sidebar */}
      <CustomAdSlot 
        placementKey="settings_sidebar" 
        className="mt-4"
      />
    </div>
  );
}
